import React, { useState, useEffect } from 'react';
import { Modal, Typography, Button, Input, useToast, Alert as SpeculoAlert, AlertDescription, Checkbox } from '@crisellevaldez/speculo-ui';
import { RolloutLinkProvider, CredentialsManager } from '@rollout/link-react';
// Import CSS URL for dynamic loading (using Vite's ?url suffix)
import rolloutCssUrl from '@rollout/link-react/style.css?url';
import '../../../styles/rollout-overrides.css';
import { Organization } from '../../../types/organization.types';
import {
  MessageSquare,
  RotateCw,
  Edit,
  // Database,
  Code,
  // Radio,
  Settings,
  // LucideFileWarning,
  // Tag,
  Link,
  Check,
  X,
  Info,
} from 'lucide-react';
import { RolloutSyncStatusBanner, RolloutSyncResults } from './RolloutSyncStatusBanner';
import { formatPhoneNumber } from '../../../utils/formatters';
import { TwilioModal } from './TwilioModal';
import { CRMModal } from './CRMModal';
import WebsiteIntegrationModal from './WebsiteIntegrationModal';
import { CINCModal } from './CINCModal';
import { SpeculoOAuthModal } from './SpeculoOAuthModal';
import { getOAuthClientInfo } from '../../../api/speculoOAuth';
import { FubStagesModal } from './FubStagesModal';
import { FollowUpBossModal } from './FollowUpBossModal';
import { TipBox } from '../onboarding/components/TipBox';
import { useUser } from '../../../hooks/useUser';
import { AdminLockOverlay } from './AdminLockOverlay';
import { useRolloutToken } from '../../../hooks/rollout/useRolloutToken';
import { useRolloutCrmConnection } from '../../../hooks/rollout/useRolloutCrmConnection';
import { validateSubdomain } from '../../../schemas/subdomain.schema';
import { useUpdateOrganizationSettings } from '../../../hooks/organizations/useUpdateOrganizationSettings';
import { useRolloutStages } from '../../../hooks/rollout/useRolloutStages';
import {
  hasAnyConnectedCrm,
  isCrmConnectedViaRollout,
} from '../../../utils/rollout';

interface IntegrationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
  hideTwilio?: boolean;
  /** When true, only shows FUB integration (for onboarding). Legacy — prefer crmFilter. */
  fubOnly?: boolean;
  /** Restrict the Rollout connector list to a single CRM (onboarding per-CRM flow) */
  crmFilter?: 'fub' | 'cinc' | 'brivity';
  /** Require AI acknowledgment before showing Rollout connect controls */
  requireAIAcknowledgement?: boolean;
  /** Optional custom copy for AI acknowledgment checkbox */
  aiAcknowledgementCopy?: string;
}

export const IntegrationsModal: React.FC<IntegrationsModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
  hideTwilio = false,
  fubOnly = false,
  crmFilter,
  requireAIAcknowledgement = true,
  aiAcknowledgementCopy = 'By checking the box and connecting your CRM and/or submitting data, you acknowledge and agree that you are solely responsible for ensuring that any contacts made available for outreach have provided appropriate consent to be contacted via automated systems, including AI-powered voice calls, dialing technology, text messaging, and email communications.\n\nYou further confirm that you have implemented appropriate tagging, filtering, and/or segmentation within your CRM to identify such contacts, and that only eligible contacts will be engaged by the system in compliance with all applicable laws, regulations, and communication consent requirements.',
}) => {
  // Derived filter: explicit crmFilter, else fubOnly → fub, else no restriction
  const effectiveCrmFilter: 'fub' | 'cinc' | 'brivity' | undefined = crmFilter || (fubOnly ? 'fub' : undefined);
  const restricted = Boolean(effectiveCrmFilter);
  const CRM_TITLES: Record<'fub' | 'cinc' | 'brivity', string> = {
    fub: 'Follow Up Boss Setup',
    cinc: 'CINC Setup',
    brivity: 'Brivity Setup',
  };
  const CRM_APP_KEYS: Record<'fub' | 'cinc' | 'brivity', string[]> = {
    fub: ['follow-up-boss'],
    cinc: ['cinc', 'cinc-pro'],
    brivity: ['brivity'],
  };

  // Get user role to determine permissions
  const { isAdmin, isSuperAdmin } = useUser();
  const { showToast } = useToast();

  // Modal states for each integration
  const [isTwilioModalOpen, setIsTwilioModalOpen] = useState(false);
  const [isCRMModalOpen, setIsCRMModalOpen] = useState(false);
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [isCINCModalOpen, setIsCINCModalOpen] = useState(false);
  const [isOAuthModalOpen, setIsOAuthModalOpen] = useState(false);
  const [isStagesModalOpen, setIsStagesModalOpen] = useState(false);
  const [isLegacyFubModalOpen, setIsLegacyFubModalOpen] = useState(false);
  // Auto-acknowledge if org already consented (in onboardingData) OR already
  // has any connected CRM via Rollout. Accept legacy camelCase
  // `crmConsentAcknowledged` from old Firestore onboarding.
  const onboardingData = selectedOrg?.onboardingData as
    | { crm_consent_acknowledged?: boolean; crmConsentAcknowledged?: boolean }
    | undefined;
  const baseOnboardingData = selectedOrg?.onboardingData ?? {};
  const hasExistingConsent = Boolean(
    onboardingData?.crm_consent_acknowledged ||
      onboardingData?.crmConsentAcknowledged ||
      hasAnyConnectedCrm({
        crmConnections: selectedOrg?.crmConnections,
        rollout: selectedOrg?.rollout,
      })
  );
  const [hasAcknowledgedAI, setHasAcknowledgedAI] = useState(hasExistingConsent);

  // FUB subdomain state - canonical home is `fubSetup.subdomain`; legacy
  // `rollout.fub.subdomain` is read as a fallback for orgs still on the old shape.
  const fubSubdomainPersisted =
    selectedOrg?.fubSetup?.subdomain || selectedOrg?.rollout?.fub?.subdomain || '';
  const [fubSubdomain, setFubSubdomain] = useState(fubSubdomainPersisted);
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(false);

  // Track local disconnect so stages hide immediately without waiting for org refresh
  const [didDisconnectFub, setDidDisconnectFub] = useState(false);

  // Reset disconnect flag when org changes
  useEffect(() => {
    setDidDisconnectFub(false);
  }, [selectedOrg?.id]);

  // Sync local state whenever the persisted consent flag changes — covers
  // selectedOrg loading after mount and the consent being saved via the
  // checkbox handler below.
  useEffect(() => {
    setHasAcknowledgedAI(hasExistingConsent);
  }, [hasExistingConsent]);

  // Super admins should never be presented with the client-consent gate —
  // they're approving on behalf of someone else and shouldn't be able to
  // accidentally accept the contact-consent declaration for the org.
  const showConsentGate = requireAIAcknowledgement && !isSuperAdmin && !hasAcknowledgedAI;

  // Check if FUB is connected via Rollout — handles new credentials[] shape
  // and legacy rollout.fub.connected shape.
  const isFubConnected =
    isCrmConnectedViaRollout(
      { crmConnections: selectedOrg?.crmConnections, rollout: selectedOrg?.rollout },
      'follow-up-boss'
    ) && !didDisconnectFub;

  const syncStatus = selectedOrg?.last_initial_sync;

  // Prefer the FUB connectionId from the typed crmConnections list (new API).
  // Fall back to orgId — the backend still resolves that to the active
  // connection for orgs whose GQL hasn't repopulated crmConnections yet.
  const fubConnectionId =
    selectedOrg?.crmConnections?.find(
      (c) => c.app === 'follow-up-boss' && (c.status === 'active' || c.status === 'connected')
    )?.connectionId ?? selectedOrg?.id;

  // Stages are sourced from the API (single source of truth) so that the
  // optimistic update in useRolloutStages can reflect a save instantly without
  // waiting for the GQL org refetch.
  const { stages: fubStages, isLoading: isLoadingFubStages } = useRolloutStages({
    orgId: fubConnectionId,
    enabled: isOpen && isFubConnected,
  });
  const enabledStagesCount = fubStages.filter((s) => s.enabled).length;
  const isSyncing = syncStatus?.status === 'processing';

  // Check if legacy FUB is connected (via API key)
  const isLegacyFubConnected = Boolean(selectedOrg?.crmApiKeyHash);

  // Dynamically load/unload Rollout CSS only when modal is open to prevent global style leaks
  useEffect(() => {
    if (isOpen) {
      // Check if CSS is already loaded
      const existingLink = document.getElementById('rollout-css');
      if (!existingLink) {
        const linkElement = document.createElement('link');
        linkElement.id = 'rollout-css';
        linkElement.rel = 'stylesheet';
        linkElement.href = rolloutCssUrl;
        document.head.appendChild(linkElement);
      }
    } else {
      // Cleanup: remove the CSS when modal closes
      const existingLink = document.getElementById('rollout-css');
      if (existingLink) {
        existingLink.remove();
      }
    }
  }, [isOpen]);

  // Rollout token for Follow Up Boss - using React Query (no useEffect needed!)
  const {
    data: rolloutToken,
    isLoading: tokenLoading,
    error: tokenError,
  } = useRolloutToken({
    orgId: selectedOrg?.id,
    enabled: isOpen, // Only fetch when modal is open
  });

  // CRM connection handlers - saves state to Firestore (rollout.<crm>.connected)
  const { handleCredentialAdded: baseHandleCredentialAdded, handleCredentialDeleted, isConnecting, isDisconnecting } =
    useRolloutCrmConnection({
      selectedOrg,
      onSuccess,
    });

  const { updateSettings, isPending: isSavingSubdomainViaApi } = useUpdateOrganizationSettings(onSuccess);

  // Wrapper to include subdomain when connecting FUB + persist consent acknowledgment
  const handleCredentialAdded = async (credential: { id: string; appKey: string }) => {
    setDidDisconnectFub(false);
    await baseHandleCredentialAdded({
      ...credential,
      subdomain: credential.appKey === 'follow-up-boss' ? fubSubdomain : undefined,
    });
    // Save consent acknowledgment to organization onboarding data
    if (hasAcknowledgedAI && selectedOrg?.id) {
      try {
        await updateSettings({
          id: selectedOrg.id,
          data: {
            onboardingData: {
              ...baseOnboardingData,
              crm_consent_acknowledged: true,
              crm_consent_acknowledged_at: new Date().toISOString(),
            },
          } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
        });
      } catch {
        // Non-blocking — consent is tracked but shouldn't break the connection flow
      }
    }
  };

  // Wrapper to hide stages immediately on disconnect
  const handleCredentialDeletedWithHide = async (credential: { id: string; appKey: string }) => {
    await handleCredentialDeleted(credential);
    if (credential.appKey === 'follow-up-boss') {
      setDidDisconnectFub(true);
    }
  };

  // Combined loading state for FUB operations
  const isFubLoading = isConnecting || isDisconnecting;

  // Twilio is configured if a phone number is set (we may not have SID/token stored)
  const isTwilioConfigured = () => {
    return Boolean(selectedOrg?.phoneNumbers?.outbound || selectedOrg?.phoneNumbers?.inbound);
  };

  // Migrating to Rollout — commented out with CINC/Brivity sections
  // const isCRMConfigured = () => {
  //   return Boolean(selectedOrg?.externalClient);
  // };

  // const isCINCConfigured = () => {
  //   return Boolean(selectedOrg?.cincSetup?.connected);
  // };

  const [oauthVerified, setOauthVerified] = useState(false);

  // Check OAuth status from the API on modal open (org.oauthSetup may be stale/null in GQL)
  useEffect(() => {
    if (!isOpen || !selectedOrg?.id) return;
    setOauthVerified(false);
    getOAuthClientInfo(selectedOrg.id)
      .then((info) => { if (info?.clientId) setOauthVerified(true); })
      .catch(() => {});
  }, [isOpen, selectedOrg?.id]);

  const isOAuthConfigured = () => {
    return oauthVerified || Boolean(
      selectedOrg?.oauthSetup?.created_at ||
      selectedOrg?.oauthSetup?.createdAt ||
      selectedOrg?.oauthSetup?.clientId ||
      selectedOrg?.oauthSetup?.client_id
    );
  };

  // Handle success from child modals
  const handleSuccess = () => {
    onSuccess?.();
  };

  const isSavingSubdomain = isSavingSubdomainViaApi;

  // Save subdomain via Remi API
  const handleSaveSubdomain = async () => {
    if (!selectedOrg?.id) return;

    const validation = validateSubdomain(fubSubdomain);
    if (!validation.isValid) {
      showToast({
        message: validation.error || 'Invalid subdomain format',
        variant: 'error',
      });
      return;
    }

    try {
      // Canonical store for FUB subdomain is `fubSetup.subdomain` — that's the
      // column the backend reads (see remi-api onboarding.py secrets-build).
      // Writing here also keeps us out of `rollout_config`, where the Rollout
      // webhook owns the credentials list and our writes risk racing with it.
      await updateSettings({
        id: selectedOrg.id,
        data: {
          fubSetup: {
            ...(selectedOrg.fubSetup ?? {}),
            subdomain: fubSubdomain?.toLowerCase() || null,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
      setIsEditingSubdomain(false);
    } catch (error) {
      console.error('Failed to save subdomain:', error);
      showToast({
        message: 'Failed to save subdomain. Please try again.',
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <Modal.Header>
          <Typography variant="h3">{effectiveCrmFilter ? CRM_TITLES[effectiveCrmFilter] : 'Integrations'}</Typography>
        </Modal.Header>
        <Modal.Body className="pb-8">
          <AdminLockOverlay locked={!isAdmin} subject="Integrations">
          <div className="space-y-3">
            {!restricted && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-gray-600 dark:text-gray-400">Configure integrations with third-party services.</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isOAuthConfigured() && (
                    <div className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded-full whitespace-nowrap">
                      Active
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsOAuthModalOpen(true)}
                    disabled={!isSuperAdmin}
                    className="flex items-center gap-2"
                  >
                    <Code className="w-4 h-4" />
                    <span className="hidden sm:inline">OAuth</span>
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-4">
              {/* Twilio Integration - Super Admin Only */}
              {isSuperAdmin && !hideTwilio && (
                <div className="border border-gray-300 dark:border-white/15 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full flex-shrink-0">
                        <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <Typography variant="h5" className="font-semibold">
                          Twilio
                        </Typography>
                        {isTwilioConfigured() && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {formatPhoneNumber(selectedOrg?.phoneNumbers?.outbound || selectedOrg?.phoneNumbers?.inbound)}
                            {' \u00b7 '}Enter credentials to update phone number
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isTwilioConfigured() && (
                        <div className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded-full whitespace-nowrap">
                          Configured
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsTwilioModalOpen(true)}
                        className="flex items-center gap-1"
                      >
                        <Edit className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {isTwilioConfigured() ? 'Update' : 'Setup'}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* CRM Integrations - Rollout OAuth */}
              <div
                className={`border border-gray-300 dark:border-white/15 rounded-lg p-4 ${
                  isLegacyFubConnected ? 'bg-gray-50 dark:bg-white/5' : ''
                }`}
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-full flex-shrink-0">
                      <RotateCw className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <Typography variant="h5" className="font-semibold">
                        CRM Integrations
                      </Typography>
                      {isLegacyFubConnected && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Please disconnect Legacy FUB first
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show blocked message when legacy FUB is connected */}
                {isLegacyFubConnected ? (
                  <div className="py-4 px-3 bg-gray-100 dark:bg-white/5 rounded border border-gray-200 dark:border-white/10">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      CRM integrations are not available while the Legacy FUB integration
                      is active. Please disconnect the Legacy integration below to use this version.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Pro tip about speculo_sync tag - only show when FUB not connected */}
                    {!isFubConnected && (
                      <div className="mb-3">
                        <TipBox>
                          <strong>Pro tip:</strong> Before connecting your CRM, we highly recommend
                          applying the{' '}
                          <code className="bg-yellow-200 dark:bg-yellow-800/40 px-1 rounded">speculo_sync</code> tag to
                          the contacts you want Remi (AI) to engage with to help speed up the
                          initial sync process especially for large databases. This tag does not
                          trigger any communication. You can still move forward without applying
                          the tag. Additional contacts can be synced over at any time after the
                          integration is established.
                        </TipBox>
                      </div>
                    )}

                    {/* Sync Status Banners - only show if FUB is connected */}
                    {isFubConnected && syncStatus && isSyncing && (
                      <RolloutSyncStatusBanner syncStatus={syncStatus} />
                    )}
                    {isFubConnected && syncStatus && !isSyncing && (
                      <RolloutSyncResults syncStatus={syncStatus} />
                    )}

                    {/* Loading state */}
                    {tokenLoading && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <RotateCw className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Loading...</span>
                      </div>
                    )}

                    {/* Error state */}
                    {tokenError && (
                      <div className="text-red-500 text-sm">
                        Failed to load CRM integrations
                      </div>
                    )}

                    {/* Rollout CredentialsManager — FUB, CINC, Brivity (always visible so user picks CRM first) */}
                    {rolloutToken && (
                      <div className={`relative rollout-container ${showConsentGate ? 'pointer-events-none opacity-60' : ''}`}>
                        {/* Loading overlay during connect/disconnect */}
                        {isFubLoading && (
                          <div className="absolute inset-0 bg-white/70 dark:bg-black/50 flex items-center justify-center z-10 rounded">
                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                              <RotateCw className="w-4 h-4 animate-spin" />
                              <span className="text-sm">
                                {isConnecting ? 'Connecting...' : 'Disconnecting...'}
                              </span>
                            </div>
                          </div>
                        )}
                        <RolloutLinkProvider token={rolloutToken}>
                          <CredentialsManager
                            apiCategories={{ crm: true }}
                            onCredentialAdded={handleCredentialAdded}
                            onCredentialDeleted={handleCredentialDeletedWithHide}
                            shouldRenderConnector={(connector: { appKey?: string }) => {
                              const allowed = effectiveCrmFilter
                                ? CRM_APP_KEYS[effectiveCrmFilter]
                                : ['follow-up-boss', 'cinc', 'cinc-pro', 'brivity', 'kvcore'];
                              return allowed.includes(connector.appKey);
                            }}
                          />
                        </RolloutLinkProvider>
                      </div>
                    )}

                    {/* Consent checkbox — hidden for super admins so they can't
                        accidentally accept the contact-consent declaration on
                        behalf of the org. */}
                    {showConsentGate && (
                      <div className="mt-3 p-4 border border-amber-200 dark:border-amber-500/30 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="crm-ai-acknowledgement"
                            checked={hasAcknowledgedAI}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              setHasAcknowledgedAI(checked);
                              if (!checked || !selectedOrg?.id) return;
                              try {
                                await updateSettings({
                                  id: selectedOrg.id,
                                  data: {
                                    onboardingData: {
                                      ...baseOnboardingData,
                                      crm_consent_acknowledged: true,
                                      crm_consent_acknowledged_at: new Date().toISOString(),
                                    },
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  } as any,
                                });
                              } catch {
                                // Non-blocking — revert local state so the user can retry
                                setHasAcknowledgedAI(false);
                              }
                            }}
                          />
                          <label htmlFor="crm-ai-acknowledgement" className="text-xs text-foreground/80 leading-relaxed cursor-pointer whitespace-pre-line">
                            {aiAcknowledgementCopy}
                          </label>
                        </div>
                      </div>
                    )}

                    {/* FUB Stages Configuration - shown when FUB is connected (hide for non-FUB filters) */}
                    {isFubConnected && (!effectiveCrmFilter || effectiveCrmFilter === 'fub') && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-white/10">
                        <Typography variant="small" className="font-semibold text-gray-500 dark:text-gray-400 mb-2">
                          Follow Up Boss
                        </Typography>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              Stages:{' '}
                              {isLoadingFubStages
                                ? 'Loading...'
                                : enabledStagesCount > 0
                                  ? `${enabledStagesCount} configured`
                                  : 'Not configured'}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsStagesModalOpen(true)}
                            className="flex items-center gap-1 h-8 px-4"
                          >
                            <Settings className="w-4 h-4" />
                            <span className="hidden sm:inline">Configure Stages</span>
                          </Button>
                        </div>

                        {/* FUB Subdomain Configuration */}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2">
                            <Link className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            {isEditingSubdomain ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  value={fubSubdomain}
                                  onChange={(e) => setFubSubdomain(e.target.value.trim().toLowerCase())}
                                  placeholder="yourcompany"
                                  className="w-32"
                                  autoFocus
                                />
                                <span className="text-xs text-gray-500 dark:text-gray-400">.followupboss.com</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                Subdomain:{' '}
                                {fubSubdomainPersisted
                                  ? `${fubSubdomainPersisted}.followupboss.com`
                                  : 'Not configured'}
                              </span>
                            )}
                          </div>
                          {isEditingSubdomain ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSaveSubdomain}
                                disabled={isSavingSubdomain}
                                className="flex items-center gap-1 h-8 px-2"
                              >
                                {isSavingSubdomain ? (
                                  <RotateCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4 text-green-600" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setFubSubdomain(fubSubdomainPersisted);
                                  setIsEditingSubdomain(false);
                                }}
                                disabled={isSavingSubdomain}
                                className="flex items-center gap-1 h-8 px-2"
                              >
                                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setIsEditingSubdomain(true)}
                              className="flex items-center gap-1 h-8 px-4"
                            >
                              <Edit className="w-4 h-4" />
                              <span className="hidden sm:inline">Edit</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Coming Soon Alert (hidden in fubOnly mode) */}
            {!restricted && (
              <SpeculoAlert variant="info">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  More CRMs coming soon (Sierra Interactive, and more)
                </AlertDescription>
              </SpeculoAlert>
            )}
          </div>
          </AdminLockOverlay>
        </Modal.Body>
      </Modal>

      {/* Child Modals */}
      <TwilioModal
        isOpen={isTwilioModalOpen}
        onClose={() => setIsTwilioModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      <CRMModal
        isOpen={isCRMModalOpen}
        onClose={() => setIsCRMModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      {/* Website Integration Instructions Modal */}
      <WebsiteIntegrationModal
        isOpen={isIntegrationModalOpen}
        onClose={() => setIsIntegrationModalOpen(false)}
        orgId={selectedOrg?.id}
      />

      <CINCModal
        isOpen={isCINCModalOpen}
        onClose={() => setIsCINCModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      <SpeculoOAuthModal
        isOpen={isOAuthModalOpen}
        onClose={() => setIsOAuthModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={() => {
          setOauthVerified(true);
          handleSuccess();
        }}
      />

      {/* FUB Stages Configuration Modal */}
      <FubStagesModal
        isOpen={isStagesModalOpen}
        onClose={() => setIsStagesModalOpen(false)}
        orgId={fubConnectionId}
        onSuccess={handleSuccess}
      />

      {/* Legacy Follow Up Boss Modal */}
      <FollowUpBossModal
        isOpen={isLegacyFubModalOpen}
        onClose={() => setIsLegacyFubModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      {/* Legacy Follow Up Boss Modal */}
      <FollowUpBossModal
        isOpen={isLegacyFubModalOpen}
        onClose={() => setIsLegacyFubModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      {/* Legacy Follow Up Boss Modal */}
      <FollowUpBossModal
        isOpen={isLegacyFubModalOpen}
        onClose={() => setIsLegacyFubModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />
    </>
  );
};
